import * as React from 'react';
import {connect} from 'react-redux';

import * as ramda from 'ramda';

import {AccountTypes} from 'nutshell-core/account-types';
import {Assignables} from 'nutshell-core/assignables';
import {safelyGetFilterByKey} from 'nutshell-core/filter';
import * as Schema from 'nutshell-core/schema';
import {Stagesets} from 'nutshell-core/stagesets';
import type {
    AccountType,
    Assignable,
    CoreEntityType,
    FilterObject,
    Stageset,
} from 'nutshell-core/types';

import {CheckboxButton} from 'shells/button/checkbox-button/checkbox-button';
import {addLeadStatusIconUrls, convertToModels} from 'shells/filter-capsule/convert-to-models';
import {FilterCapsule} from 'shells/filter-capsule/filter-capsule';
// @ts-expect-error
import {StepIcon} from 'shells/icon/svg/step-icon';

import {getEntityGraduatedFilters} from '../../../entities/entities-constants';
import type {NutshellState} from '../../../store';

import {
    getCreatorOriginCategoryFromDisplayName,
    getIconByCreatorOriginCategory,
} from '../columns/cell/helpers';

const breakPoint = {
    leads: 1200,
    accounts: 1000,
    contacts: 700,
} as const;

type OwnProps = {
    activeFilters: FilterObject[];
    schema: Schema.Properties;
    entityType: CoreEntityType;
    addFilters: (arg1: FilterObject) => any;
    updateFilter: (arg1: FilterObject, arg2: FilterObject) => any;
    removeFilters: (arg1: FilterObject[]) => any;
};

type StateProps = {
    accountTypes: AccountType[];
    assignables: Assignable[];
    stagesets: Stageset[];
};

type Props = OwnProps & StateProps;

type State = {
    maxFiltersShownPerCapsule: number;
};

function mapStateToProps(state: NutshellState): StateProps {
    return {
        accountTypes: AccountTypes.getAll(state),
        assignables: Assignables.getAll(state),
        stagesets: Stagesets.getFilterableStagesets(state),
    };
}

export class EntityListGraduatedFiltersComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            maxFiltersShownPerCapsule: window.innerWidth > breakPoint[props.entityType] ? 2 : 1,
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    render() {
        const graduatedFilters = getEntityGraduatedFilters(this.props.entityType);

        if (!ramda.isEmpty(this.props.schema) && !ramda.isEmpty(graduatedFilters)) {
            // Running an additional filter function here because if an instance
            // does not have "Multiple Pipelines" enabled, we'll manually
            // tweak the schema on the server and flip the `stageset` isDisplayable
            // boolean to `false`. If that's the case, we need to hide it.
            const displayableFilters = graduatedFilters.filter((gradFilter) => {
                return this.props.schema[gradFilter] && this.props.schema[gradFilter].isDisplayable;
            });

            return (
                <>
                    {displayableFilters.map((filterName: string) => {
                        let collection;
                        if (filterName === 'status') {
                            // Just for flow
                            if (
                                Boolean(this.props.schema) &&
                                this.props.schema.status &&
                                this.props.schema.status.type === 'enum'
                            ) {
                                // @ts-expect-error
                                collection = convertToModels(this.props.schema.status.enum);
                                collection = addLeadStatusIconUrls(collection);
                            }
                        }
                        if (filterName === 'accountType') {
                            collection = convertToModels(this.props.accountTypes);
                        }
                        if (filterName === 'owner') {
                            collection = convertToModels(this.props.assignables);
                        }
                        if (filterName === 'stageset') {
                            collection = convertToModels(
                                this.props.stagesets.map((stageset) => {
                                    return {
                                        ...stageset,
                                        isEnabled: stageset.canAccess && stageset.status !== 3,
                                    };
                                })
                            );
                        }
                        if (filterName === 'creatorOriginCategory') {
                            const schemaField = this.props.schema.creatorOriginCategory;
                            if (schemaField && schemaField.type === 'enum' && schemaField.enum) {
                                // @ts-expect-error
                                collection = convertToModels(schemaField.enum);
                                collection = (collection || []).map((item) => {
                                    const categoryKey = getCreatorOriginCategoryFromDisplayName(
                                        item.name
                                    );
                                    const IconComponent =
                                        categoryKey && getIconByCreatorOriginCategory(categoryKey);

                                    return {
                                        ...item,
                                        icon:
                                            (IconComponent && (
                                                <IconComponent size={16} alignCenter={true} />
                                            )) ??
                                            undefined,
                                    };
                                });
                            }
                        }

                        if (NutClientConfig.hasHotLeads && filterName === 'priority') {
                            return (
                                <CheckboxButton
                                    key={filterName}
                                    label='Only hot'
                                    checked={Boolean(
                                        safelyGetFilterByKey(this.props.activeFilters, filterName)
                                    )}
                                    onChange={this.handlePriorityChange}
                                />
                            );
                        }

                        return (
                            <FilterCapsule
                                icon={filterName === 'stageset' ? StepIcon : undefined}
                                hasSearch={
                                    filterName !== 'status' &&
                                    filterName !== 'creatorOriginCategory'
                                }
                                key={filterName}
                                size='big'
                                collection={collection}
                                filter={safelyGetFilterByKey(this.props.activeFilters, filterName)}
                                filterName={filterName}
                                schemaField={this.props.schema[filterName]}
                                numDisplayed={this.state.maxFiltersShownPerCapsule}
                                onSubmit={this.handleFilterUpdate}
                                onRemove={this.handleFilterRemoval}
                                hasRightMargin={false}
                            />
                        );
                    })}
                </>
            );
        }

        return <div />;
    }

    /*
     * Takes a filterKey (read: `owner`, `confidence`) and removes that
     * filter from the current set of filters.
     */
    handleFilterRemoval = (filterKey: string) => {
        const filterToRemove = this.props.activeFilters.find((filterObj) =>
            Object.prototype.hasOwnProperty.call(filterObj, filterKey)
        );
        if (filterToRemove) {
            this.props.removeFilters([filterToRemove]);
        }
    };

    handleResize = () => {
        if (
            this.state.maxFiltersShownPerCapsule === 1 &&
            window.innerWidth > breakPoint[this.props.entityType]
        ) {
            this.setState({maxFiltersShownPerCapsule: 2});
        } else if (
            this.state.maxFiltersShownPerCapsule === 2 &&
            window.innerWidth <= breakPoint[this.props.entityType]
        ) {
            this.setState({maxFiltersShownPerCapsule: 1});
        }
    };

    handlePriorityChange = (isChecked: boolean) => {
        if (!isChecked) {
            this.handleFilterRemoval('priority');
        } else {
            const data = {
                priority: {
                    anyAll: 'any',
                    data: [
                        {
                            data: 'Hot',
                        },
                    ],
                },
            } as const;

            this.handleFilterUpdate(data, 'priority');
        }
    };

    handleFilterUpdate = (data: FilterObject, filterKey: string) => {
        const oldFilter = this.props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === filterKey;
        });
        if (oldFilter) {
            this.props.updateFilter(oldFilter, data);
        } else {
            this.props.addFilters(data);
        }
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps);
export const EntityListGraduatedFilters = connector(EntityListGraduatedFiltersComponent);
